/**
 * Handover Service
 * Handles all handover and handover details-related API calls
 */
import { httpClient } from "./httpClient";

export const handoverService = {
  /**
   * Get all handovers
   * @returns {Promise<Array>} List of handovers
   */
  getAll: () => httpClient("/handovers"),

  getPaged: (query = {}) => {
    const params = new URLSearchParams();
    if (query.page != null) params.set("page", String(query.page));
    if (query.pageSize != null) params.set("pageSize", String(query.pageSize));
    if (query.search) params.set("search", query.search);
    if (query.sortBy) params.set("sortBy", query.sortBy);
    if (query.sortDirection) params.set("sortDirection", query.sortDirection);
    if (query.roomID != null && query.roomID !== "") params.set("roomID", String(query.roomID));
    if (query.productionLineID != null && query.productionLineID !== "") params.set("productionLineID", String(query.productionLineID));

    const queryString = params.toString();
    return httpClient(`/handovers/paged${queryString ? `?${queryString}` : ""}`);
  },

  /**
   * Get a specific handover by ID
   * @param {number} id - Handover ID
   * @returns {Promise<object>} Handover details
   */
  getById: (id) => httpClient(`/handovers/${id}`),

  /**
   * Create a new handover
   * @param {object} data - Handover data {roomID, handoverDate, deliveredBy, receivedBy, notes}
   * @returns {Promise<object>} Created handover
   */
  create: (data) =>
    httpClient("/handovers", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  /**
   * Update an existing handover
   * @param {number} id - Handover ID
   * @param {object} data - Updated handover data
   * @returns {Promise<object>} Updated handover
   */
  update: (id, data) =>
    httpClient(`/handovers/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  /**
   * Delete a handover
   * @param {number} id - Handover ID
   * @returns {Promise<null>} No content response
   */
  delete: (id) =>
    httpClient(`/handovers/${id}`, {
      method: "DELETE",
    }),

  /**
   * Get all handovers for a room
   * @param {number} roomId - Room ID
   * @returns {Promise<Array>} List of handovers for room
   */
  getByRoom: (roomId) => httpClient(`/handovers/by-room/${roomId}`),

  /**
   * Get all details for a handover
   * @param {number} handoverId - Handover ID
   * @returns {Promise<Array>} List of handover details
   */
  getDetails: (handoverId) =>
    httpClient(`/handovers/${handoverId}/details`),

  /**
   * Add a detail to a handover
   * @param {number} handoverId - Handover ID
   * @param {object} data - Detail data {assetID, quantity, conditionAtHandover, remarks}
   * @returns {Promise<object>} Created handover detail
   */
  addDetail: (handoverId, data) =>
    httpClient(`/handovers/${handoverId}/details`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  /**
   * Get a specific handover detail by ID
   * @param {number} detailId - Handover detail ID
   * @returns {Promise<object>} Handover detail
   */
  getDetailById: (detailId) =>
    httpClient(`/handovers/details/${detailId}`),

  /**
   * Update a handover detail
   * @param {number} detailId - Handover detail ID
   * @param {object} data - Updated detail data
   * @returns {Promise<object>} Updated handover detail
   */
  updateDetail: (detailId, data) =>
    httpClient(`/handovers/details/${detailId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  /**
   * Delete a handover detail
   * @param {number} detailId - Handover detail ID
   * @returns {Promise<null>} No content response
   */
  deleteDetail: (detailId) =>
    httpClient(`/handovers/details/${detailId}`, {
      method: "DELETE",
    }),

  /**
   * Delete multiple handovers
   * @param {Array<number>} ids - Array of handover IDs to delete
   * @returns {Promise<null>} No content response
   */
  bulkDelete: (ids) =>
    httpClient(`/handovers/bulk`, {
      method: "DELETE",
      body: JSON.stringify({ ids }),
    }),

  /**
   * Get all assets in the spare parts virtual room
   * @returns {Promise<Array>} List of spare part assets
   */
  getSparePartAssets: () =>
    httpClient(`/handovers/spare-part-assets`),

  /**
   * Get all asset items across all types (Spare, Room, Production)
   * @returns {Promise<Array>} List of all asset items
   */
  getAllAssetItems: () =>
    httpClient(`/handovers/all-asset-items`),
};

export default handoverService;
